// src/pages/Vendors/Vendors.js
import React, { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import { PlusIcon, PencilIcon, TrashIcon } from '@heroicons/react/24/outline';
import { vendorService } from '../../services/api';
import VendorForm from './VendorForm';
import VendorsWithParts from './VendorsWithParts';

const Vendors = () => {
    const [vendors, setVendors] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [currentVendor, setCurrentVendor] = useState(null);
    const [activeTab, setActiveTab] = useState('all'); // 'all' or 'withParts'

    useEffect(() => {
        fetchVendors();
    }, []);

    const fetchVendors = async () => {
        try {
            setLoading(true);
            const response = await vendorService.getVendors();
            setVendors(response);
            setError(null);
        } catch (err) {
            console.error('Error fetching vendors:', err);
            setError('Failed to load vendors. Please try again later.');
            toast.error('Failed to load vendors');
        } finally {
            setLoading(false);
        }
    };

    const handleOpenModal = (vendor = null) => {
        setCurrentVendor(vendor);
        setIsModalOpen(true);
    };

    const handleCloseModal = () => {
        setCurrentVendor(null);
        setIsModalOpen(false);
    };

    const handleSave = async (formData) => {
        try {
            if (currentVendor) {
                // Update existing vendor
                await vendorService.updateVendor(currentVendor.id, formData);
                toast.success('Vendor updated successfully');
            } else {
                // Create new vendor
                await vendorService.createVendor(formData);
                toast.success('Vendor created successfully');
            }
            fetchVendors();
            handleCloseModal();
        } catch (err) {
            console.error('Error saving vendor:', err);
            toast.error(err.response?.data?.errors ? Object.values(err.response.data.errors).flat().join(', ') : 'Error saving vendor');
        }
    };

    const handleDelete = async (id) => {
        if (!window.confirm('Are you sure you want to delete this vendor? This action cannot be undone.')) {
            return;
        }

        try {
            await vendorService.deleteVendor(id);
            toast.success('Vendor deleted successfully');
            fetchVendors();
        } catch (err) {
            console.error('Error deleting vendor:', err);
            toast.error('Failed to delete vendor');
        }
    };

    if (loading && activeTab === 'all') {
        return (
            <div className="flex items-center justify-center h-full">
                <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary-500"></div>
            </div>
        );
    }

    if (error && activeTab === 'all') {
        return (
            <div className="bg-red-100 border-l-4 border-red-500 text-red-700 p-4 rounded shadow-md">
                <p className="font-bold">Error</p>
                <p>{error}</p>
            </div>
        );
    }

    return (
        <div className="space-y-6">
            <div className="flex justify-between items-center">
                <h1 className="text-2xl font-semibold text-gray-800">Vendors</h1>
                <button
                    onClick={() => handleOpenModal()}
                    className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
                >
                    <PlusIcon className="h-5 w-5 mr-2" /> Add Vendor
                </button>
            </div>

            {/* Tabs */}
            <div className="border-b border-gray-200">
                <nav className="-mb-px flex space-x-8">
                    <button
                        className={`${
                            activeTab === 'all'
                            ? 'border-primary-500 text-primary-600'
                            : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                        } whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm`}
                        onClick={() => setActiveTab('all')}
                    >
                        All Vendors
                    </button>
                    <button
                        className={`${
                            activeTab === 'withParts'
                            ? 'border-primary-500 text-primary-600'
                            : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                        } whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm`}
                        onClick={() => setActiveTab('withParts')}
                    >
                        Vendors with Parts
                    </button>
                </nav>
            </div>

            {activeTab === 'withParts' ? (
                <VendorsWithParts />
            ) : (
                 <>
                     {/* Mobile View (Card Layout) */}
                     <div className="md:hidden space-y-4">
                         {vendors.length > 0 ? (
                             vendors.map((vendor) => (
                                 <div key={vendor.id} className="bg-white shadow overflow-hidden rounded-lg">
                                     <div className="px-4 py-5 sm:px-6 flex justify-between items-start">
                                         <div>
                                             <h3 className="text-lg leading-6 font-medium text-gray-900">{vendor.name}</h3>
                                             {vendor.is_active ? (
                                                 <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-green-100 text-green-800">
                                                    Active
                                                </span>
                                             ) : (
                                                  <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-gray-100 text-gray-800">
                                                    Inactive
                                                </span>
                                              )}
                                         </div>
                                         <div className="flex space-x-2">
                                             <button
                                                 onClick={() => handleOpenModal(vendor)}
                                                 className="text-indigo-600 hover:text-indigo-900"
                                             >
                                                 <PencilIcon className="h-5 w-5" />
                                             </button>
                                             <button
                                                 onClick={() => handleDelete(vendor.id)}
                                                 className="text-red-600 hover:text-red-900"
                                             >
                                                 <TrashIcon className="h-5 w-5" />
                                             </button>
                                         </div>
                                     </div>
                                     <div className="border-t border-gray-200 px-4 py-5 sm:p-0">
                                         <dl className="sm:divide-y sm:divide-gray-200">
                                             {vendor.contact_person && (
                                                 <div className="py-3 sm:py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
                                                     <dt className="text-sm font-medium text-gray-500">Contact Person</dt>
                                                     <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">{vendor.contact_person}</dd>
                                                 </div>
                                             )}
                                             {vendor.email && (
                                                 <div className="py-3 sm:py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
                                                     <dt className="text-sm font-medium text-gray-500">Email</dt>
                                                     <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">{vendor.email}</dd>
                                                 </div>
                                             )}
                                             {vendor.phone && (
                                                 <div className="py-3 sm:py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
                                                     <dt className="text-sm font-medium text-gray-500">Phone</dt>
                                                     <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">{vendor.phone}</dd>
                                                 </div>
                                             )}
                                             {vendor.website && (
                                                 <div className="py-3 sm:py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
                                                     <dt className="text-sm font-medium text-gray-500">Website</dt>
                                                     <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">
                                                         <a href={vendor.website} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">
                                                             {vendor.website}
                                                         </a>
                                                     </dd>
                                                 </div>
                                             )}
                                         </dl>
                                     </div>
                                 </div>
                             ))
                         ) : (
                              <div className="bg-white shadow overflow-hidden sm:rounded-lg p-6 text-center text-gray-500">
                                  No vendors found. Click 'Add Vendor' to create one.
                              </div>
                          )}
                     </div>

                     {/* Desktop View (Table Layout) */}
                     <div className="hidden md:block bg-white shadow overflow-hidden sm:rounded-lg">
                         <table className="min-w-full divide-y divide-gray-200">
                             <thead className="bg-gray-50">
                             <tr>
                                 <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                     Name
                                 </th>
                                 <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                     Contact Person
                                 </th>
                                 <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                     Email
                                 </th>
                                 <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                     Phone
                                 </th>
                                 <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                     Status
                                 </th>
                                 <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                     Actions
                                 </th>
                             </tr>
                             </thead>
                             <tbody className="bg-white divide-y divide-gray-200">
                             {vendors.length > 0 ? (
                                 vendors.map((vendor) => (
                                     <tr key={vendor.id}>
                                         <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                                             {vendor.name}
                                         </td>
                                         <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                             {vendor.contact_person || '—'}
                                         </td>
                                         <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                             {vendor.email || '—'}
                                         </td>
                                         <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                             {vendor.phone || '—'}
                                         </td>
                                         <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                             {vendor.is_active ? (
                                                 <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-green-100 text-green-800">
                                                        Active
                                                    </span>
                                             ) : (
                                                  <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-gray-100 text-gray-800">
                                                        Inactive
                                                    </span>
                                              )}
                                         </td>
                                         <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                                             <div className="flex space-x-2">
                                                 <button
                                                     onClick={() => handleOpenModal(vendor)}
                                                     className="text-indigo-600 hover:text-indigo-900"
                                                 >
                                                     <PencilIcon className="h-5 w-5" />
                                                 </button>
                                                 <button
                                                     onClick={() => handleDelete(vendor.id)}
                                                     className="text-red-600 hover:text-red-900"
                                                 >
                                                     <TrashIcon className="h-5 w-5" />
                                                 </button>
                                             </div>
                                         </td>
                                     </tr>
                                 ))
                             ) : (
                                  <tr>
                                      <td colSpan="6" className="px-6 py-4 text-center text-sm text-gray-500">
                                          No vendors found.
                                      </td>
                                  </tr>
                              )}
                             </tbody>
                         </table>
                     </div>
                 </>
             )}

            {isModalOpen && (
                <VendorForm
                    isOpen={isModalOpen}
                    onClose={handleCloseModal}
                    onSave={handleSave}
                    vendor={currentVendor}
                />
            )}
        </div>
    );
};

export default Vendors;
